import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Eye, EyeOff, Lock, Mail, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setSubmitting(true);
        setError(null);
        try {
            await login(email.trim(), password);
            toast.success("Welcome back");
            navigate(from, { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 429) {
                setError(detail || "Too many failed attempts. Try again in 15 minutes.");
            } else if (status === 401) {
                setError("Invalid email or password.");
            } else {
                setError("Could not sign in. Check your connection and try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(at_top_left,hsl(var(--primary)/0.08),transparent_50%),radial-gradient(at_bottom_right,hsl(var(--accent)/0.08),transparent_50%)]" />
            <div className="w-full max-w-md px-6">
                <div className="text-center mb-8">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
                        Montenegro Sea View
                    </div>
                    <h1 className="font-grotesk font-semibold text-3xl">Flip Finder</h1>
                    <p className="text-xs text-muted-foreground mt-2">
                        Private investment desk — sign in to continue.
                    </p>
                </div>
                <Card className="p-6 backdrop-blur-sm bg-card/80 border-border/60">
                    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="login-email" className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                Email
                            </Label>
                            <div className="relative">
                                <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    id="login-email"
                                    type="email"
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9"
                                    placeholder="admin@example.com"
                                    data-testid="login-email"
                                    required
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="login-password" className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                Password
                            </Label>
                            <div className="relative">
                                <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    id="login-password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-9 pr-9"
                                    data-testid="login-password"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    data-testid="toggle-password-visibility"
                                >
                                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-xs"
                                data-testid="login-error"
                            >
                                <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting || !email || !password}
                            className="w-full"
                            data-testid="login-submit"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>
                </Card>
                <p className="text-[10px] text-center text-muted-foreground mt-6 tracking-wider uppercase">
                    Private dashboard · Real listings · No demo data
                </p>
            </div>
        </div>
    );
};

export default Login;
